import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  getReadyMadeDesignsPage,
  readyMadeDisplayUrl,
  readyMadeToFlowSelectedImage,
  readFlowSelectedProduct,
  clearFlowSelectedProduct,
} from '../apiClient';
import { ShoppingBag, ArrowRight, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import ProductSelectionFlowBanner from './ProductSelectionFlowBanner';

const SEARCH_DEBOUNCE_MS = 350;
const PAGE_SIZE = 12;

function mapDesignRow(d) {
  return {
    id: `api-${d.id}`,
    dbId: d.id,
    name: d.name,
    imageUrl: readyMadeDisplayUrl(d),
    artUrl: d.image_url,
    mockupUrl: d.mockup_image_url || null,
    style: d.style,
    category: d.category || 'Preset',
    price: d.price || '$29.99',
    status: 'Active',
  };
}

const ReadyMadeGallery = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const selectedProduct =
    location.state?.selectedProduct ?? readFlowSelectedProduct()?.selectedProduct ?? null;
  const [designs, setDesigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [pageIndex, setPageIndex] = useState(0);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const fetchPage = useCallback(async (page, q) => {
    setLoading(true);
    setError(null);
    try {
      const offset = page * PAGE_SIZE;
      const params = { limit: PAGE_SIZE, offset };
      if (q) params.q = q;
      const data = await getReadyMadeDesignsPage(params);
      const items = Array.isArray(data?.items) ? data.items : [];
      setDesigns(items.map(mapDesignRow));
      setTotal(typeof data?.total === 'number' ? data.total : items.length);
    } catch {
      setError('Could not load designs. Please try again.');
      setDesigns([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setPageIndex(0);
  }, [debouncedSearch]);

  useEffect(() => {
    fetchPage(pageIndex, debouncedSearch);
  }, [pageIndex, debouncedSearch, fetchPage]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const rangeStart = total === 0 ? 0 : pageIndex * PAGE_SIZE + 1;
  const rangeEnd = Math.min((pageIndex + 1) * PAGE_SIZE, total);

  const handleBuyNow = (design) => {
    const flow = readyMadeToFlowSelectedImage({
      id: design.dbId,
      name: design.name,
      image_url: design.artUrl,
      style: design.style,
      mockup_image_url: design.mockupUrl ?? null,
    });
    if (selectedProduct) {
      clearFlowSelectedProduct();
      navigate('/configure-mockup', {
        state: { selectedProduct, selectedImage: flow },
      });
      return;
    }
    navigate('/products', {
      state: { selectedImage: flow },
    });
  };

  const goToPage = (next) => {
    if (next < 0 || next >= totalPages || next === pageIndex) return;
    setPageIndex(next);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-12">
        <div className="text-center mb-10">
          <h1 className="text-5xl font-extrabold text-gray-900 mb-4 tracking-tight">Ready-Made Collection</h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            {selectedProduct
              ? 'Pick a design for the product you selected. No account needed.'
              : 'Explore our curated collection of premium designs. Generated by AI, curated by us. Ready to ship.'}
          </p>
        </div>
        {selectedProduct && (
          <div className="mb-8">
            <ProductSelectionFlowBanner
              selectedProduct={selectedProduct}
              productImageUrl={selectedProduct.image || selectedProduct.thumbnail}
            />
          </div>
        )}

        <div className="max-w-xl mx-auto mb-10">
          <label htmlFor="ready-made-search" className="sr-only">
            Search designs
          </label>
          <div className="relative">
            <Search
              className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
              size={20}
              aria-hidden
            />
            <input
              id="ready-made-search"
              type="search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by design name…"
              className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-200 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              autoComplete="off"
            />
          </div>
        </div>

        {!loading && !error && total > 0 && (
          <p className="text-center text-sm text-gray-500 mb-6">
            Showing {rangeStart}–{rangeEnd} of {total}
            {debouncedSearch ? ` matching “${debouncedSearch}”` : ''}
          </p>
        )}

        {loading && (
          <p className="text-center text-gray-500 py-8">Loading designs…</p>
        )}
        {error && (
          <p className="text-center text-red-500 py-8">{error}</p>
        )}
        {!loading && !error && designs.length === 0 && (
          <p className="text-center text-gray-500 py-8">
            {debouncedSearch
              ? `No designs match “${debouncedSearch}”.`
              : 'No ready-made designs are available yet.'}
          </p>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {designs.map((design) => (
            <div
              key={design.id}
              className="bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden group border border-gray-100"
            >
              <div className="relative h-80 bg-gray-100 overflow-hidden">
                <img
                  src={design.imageUrl}
                  alt={design.name}
                  className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col items-center justify-center gap-4">
                  <button
                    type="button"
                    onClick={() => handleBuyNow(design)}
                    className="bg-white text-gray-900 px-8 py-3 rounded-full font-bold flex items-center gap-2 hover:bg-blue-50 hover:scale-105 transition-all duration-200 shadow-lg"
                  >
                    <ShoppingBag size={20} />
                    {selectedProduct ? 'Use this design' : 'Buy Now'}
                  </button>
                </div>
              </div>
              <div className="p-6">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="text-lg font-bold text-gray-900 leading-tight line-clamp-2 min-h-[3rem]">
                    {design.name}
                  </h3>
                  <span className="bg-blue-100 text-blue-800 text-xs font-semibold px-2.5 py-0.5 rounded mr-2 whitespace-nowrap">
                    {design.category}
                  </span>
                </div>
                <div className="flex justify-between items-center mt-4">
                  <span className="text-2xl font-bold text-gray-900">{design.price}</span>
                  <button
                    type="button"
                    onClick={() => handleBuyNow(design)}
                    className="text-blue-600 hover:text-blue-800 font-semibold text-sm flex items-center gap-1 group/link"
                  >
                    Customize{' '}
                    <ArrowRight
                      size={16}
                      className="group-hover/link:translate-x-1 transition-transform"
                    />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {!loading && !error && totalPages > 1 && (
          <nav
            className="flex flex-wrap items-center justify-center gap-4 mt-12"
            aria-label="Ready-made pagination"
          >
            <button
              type="button"
              onClick={() => goToPage(pageIndex - 1)}
              disabled={pageIndex === 0 || loading}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg border border-gray-200 bg-white text-gray-700 font-medium hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={18} />
              Previous
            </button>
            <span className="text-sm text-gray-600 font-medium px-2">
              Page {pageIndex + 1} of {totalPages}
            </span>
            <button
              type="button"
              onClick={() => goToPage(pageIndex + 1)}
              disabled={pageIndex >= totalPages - 1 || loading}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg border border-gray-200 bg-white text-gray-700 font-medium hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next
              <ChevronRight size={18} />
            </button>
          </nav>
        )}
      </div>
    </div>
  );
};

export default ReadyMadeGallery;
